// src/components/padrekko/qc/AttachmentSection.jsx
//
// Attachment surface reused at two scopes:
//   - finding scope (findingId set): photos only — drone/field evidence on a
//     specific finding.
//   - pad scope (findingId null): photos + documents (PDF/DOCX/XLSX) — general
//     pad context / things the AI missed.
//
// Renders the existing attachments (photo thumbnails → lightbox; documents →
// presigned download link) plus an upload control gated on case writeability.
// The server is the source of truth for validation, auth, and the close
// lockdown; the client checks here are UX niceties (fast, friendly messages).

import { useRef, useState } from "react";
import { Loader2, Plus, X as XIcon, FileText, Image as ImageIcon, Trash2, AlertCircle } from "lucide-react";
import {
  useQcStore,
  attachmentsForFinding,
  attachmentsForPad,
} from "../../../stores/useQcStore";
import { useAuthStore } from "../../../stores/useAuthStore";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";
import PhotoLightbox from "./PhotoLightbox";

const MAX_BYTES = 20 * 1024 * 1024;
const PHOTO_ACCEPT = "image/jpeg,image/png";
const DOC_ACCEPT =
  ".pdf,.docx,.xlsx,application/pdf," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const PHOTO_MIME = new Set(["image/jpeg", "image/png"]);
const DOC_MIME = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

function fmtSize(b) {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Client-side pre-check. Returns an error string or null. file.type can be
// empty for some pickers — when it is, defer to the server rather than block.
function preCheck(file, isFindingScope) {
  if (file.size > MAX_BYTES) return "File exceeds the 20 MB limit.";
  if (/heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)) {
    return "HEIC not supported, please convert to JPEG/PNG before upload.";
  }
  if (!file.type) return null; // unknown — let the server validate by magic bytes
  const isPhoto = PHOTO_MIME.has(file.type);
  const isDoc = DOC_MIME.has(file.type);
  if (isFindingScope && !isPhoto) return "Only JPG or PNG photos attach to a finding.";
  if (!isFindingScope && !isPhoto && !isDoc) return "Allowed: JPG, PNG, PDF, DOCX, XLSX.";
  return null;
}

export default function AttachmentSection({ padId, findingId = null, compact = false }) {
  const isFindingScope = !!findingId;

  const attachments = useQcStore((s) => s.attachments);
  const caseWriteable = useQcStore((s) => s.caseWriteable);
  const caseComplete = useQcStore((s) => s.caseInfo?.current_stage === "complete");
  const uploadAttachment = useQcStore((s) => s.uploadAttachment);
  const deleteAttachment = useQcStore((s) => s.deleteAttachment);
  const uploadInflight = useQcStore((s) => s.attachmentUploadInflight);
  const deleteInflightId = useQcStore((s) => s.attachmentDeleteInflightId);

  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isServiceProvider = useEffectiveOrg()?.is_service_provider === true;

  const rows = isFindingScope
    ? attachmentsForFinding(attachments, findingId)
    : attachmentsForPad(attachments, padId);
  const photos = rows.filter((a) => a.attachment_type === "photo");
  const docs = rows.filter((a) => a.attachment_type === "document");

  const fileInputRef = useRef(null);
  const [pending, setPending] = useState(null); // { file }
  const [caption, setCaption] = useState("");
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // Delete visibility mirrors the backend rule: uploader or service-provider;
  // after the case is complete, only the service-provider (DH steward).
  function canDelete(att) {
    if (caseComplete) return isServiceProvider;
    return att.uploaded_by === userId || isServiceProvider;
  }

  function handlePick(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    const err = preCheck(file, isFindingScope);
    if (err) {
      setError(err);
      setPending(null);
      return;
    }
    setError(null);
    setCaption("");
    setPending({ file });
  }

  async function handleUpload() {
    if (!pending) return;
    const result = await uploadAttachment({
      padId,
      findingId,
      file: pending.file,
      caption: caption.trim() || null,
    });
    if (result.ok) {
      setPending(null);
      setCaption("");
      setError(null);
    } else {
      setError(result.error || "Upload failed.");
    }
  }

  async function handleDelete(att) {
    const result = await deleteAttachment(att.id);
    if (!result.ok) setError(result.error || "Delete failed.");
  }

  const addLabel = isFindingScope ? "Add photo" : "Add attachment";
  const accept = isFindingScope ? PHOTO_ACCEPT : `${PHOTO_ACCEPT},${DOC_ACCEPT}`;
  const showAdd = caseWriteable; // server enforces; this is the visibility gate
  const hasAny = rows.length > 0;

  if (!hasAny && !showAdd) return null; // nothing to show, nothing to add

  return (
    <div className={compact ? "mt-2" : "mt-2 pt-2 border-t border-slate-800/40"}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
          {isFindingScope ? "Photos" : "Attachments"}
          {hasAny ? ` · ${rows.length}` : ""}
        </div>
        {showAdd && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadInflight}
            className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-100
                       disabled:opacity-40 transition"
            title={addLabel}
          >
            {uploadInflight ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
            {addLabel}
          </button>
        )}
      </div>

      {/* Photo thumbnails */}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {photos.map((p, i) => (
            <div key={p.id} className="relative group">
              <button
                type="button"
                onClick={() => setLightbox({ index: i })}
                className="block w-14 h-14 rounded border border-slate-700 overflow-hidden
                           bg-slate-900 hover:border-slate-500 transition"
                title={p.caption || p.filename}
              >
                <img src={p.thumb_sm_url} alt={p.caption || p.filename}
                     className="w-full h-full object-cover" loading="lazy" />
              </button>
              {/* Annotated badge */}
              {Array.isArray(p.annotations) && p.annotations.length > 0 && (
                <span
                  className="absolute bottom-0.5 left-0.5 px-1 rounded-sm bg-black/70 text-[8px]
                             font-semibold text-[#16f2d4] leading-tight pointer-events-none"
                  title={`${p.annotations.length} annotation${p.annotations.length === 1 ? "" : "s"}`}
                >
                  ✎{p.annotations.length}
                </span>
              )}
              {canDelete(p) && (
                <button
                  type="button"
                  onClick={() => handleDelete(p)}
                  disabled={deleteInflightId === p.id}
                  className="absolute -top-1 -right-1 p-0.5 rounded-full bg-slate-900 border border-slate-600
                             text-slate-400 hover:text-red-300 opacity-0 group-hover:opacity-100
                             disabled:opacity-100 transition"
                  title="Delete photo"
                >
                  {deleteInflightId === p.id
                    ? <Loader2 size={9} className="animate-spin" />
                    : <Trash2 size={9} />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <div className="space-y-1 mb-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-1.5 text-[10px]">
              <FileText size={11} className="text-fg-secondary shrink-0" />
              <a
                href={d.download_url}
                target="_blank"
                rel="noreferrer"
                className="text-slate-200 hover:text-[#16f2d4] truncate flex-1 min-w-0"
                title={d.caption ? `${d.filename} — ${d.caption}` : d.filename}
              >
                {d.filename}
              </a>
              <span className="text-fg-secondary tabular-nums shrink-0">{fmtSize(d.size_bytes)}</span>
              {canDelete(d) && (
                <button
                  type="button"
                  onClick={() => handleDelete(d)}
                  disabled={deleteInflightId === d.id}
                  className="text-fg-secondary hover:text-red-300 disabled:opacity-40 transition shrink-0"
                  title="Delete document"
                >
                  {deleteInflightId === d.id
                    ? <Loader2 size={9} className="animate-spin" />
                    : <Trash2 size={10} />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pending upload — caption + confirm */}
      {pending && (
        <div className="rounded border border-slate-700 bg-slate-900/60 p-2 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-300 min-w-0">
            {PHOTO_MIME.has(pending.file.type) ? <ImageIcon size={11} /> : <FileText size={11} />}
            <span className="truncate flex-1 min-w-0">{pending.file.name}</span>
            <span className="text-fg-secondary tabular-nums">{fmtSize(pending.file.size)}</span>
          </div>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={uploadInflight}
            placeholder="Caption (optional)"
            className="w-full text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900
                       text-slate-100 placeholder:text-slate-600 focus:outline-none
                       focus:border-slate-500 disabled:opacity-50"
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => { setPending(null); setCaption(""); setError(null); }}
              disabled={uploadInflight}
              className="text-[10px] px-2 py-0.5 rounded border border-slate-700 text-slate-400
                         hover:bg-slate-800 disabled:opacity-40 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploadInflight}
              className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                         hover:bg-[#16f2d4]/25 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {uploadInflight && <Loader2 size={9} className="animate-spin" />}
              {uploadInflight ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug mt-1">
          <AlertCircle size={10} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handlePick}
        className="hidden"
      />

      {lightbox && (
        <PhotoLightbox
          photos={photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
